import React, { useState } from 'react';
import {
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { openConversation as openChatwootConversation } from '../config/chatwoot';
import * as XLSX from 'xlsx';
import { metricsService } from '../services/api';

// Função para formatar minutos
const formatMinutes = (minutes) => {
  // Garante que não seja negativo ou inválido
  if (!minutes || isNaN(minutes) || minutes < 0) return '-';

  // Menos de 1 hora: apenas minutos
  if (minutes < 60) return `${Math.round(minutes)}min`;

  const totalHours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  // Menos de 1 dia: formato hh:mm
  if (totalHours < 24) {
    return `${String(totalHours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }

  // 1 dia ou mais: formato "Xd hh:mm"
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  return `${days}d ${String(remainingHours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// Função para obter cor do tempo de espera
const getWaitingTimeColor = (minutes) => {
  if (!minutes || isNaN(minutes)) return 'text-gray-400';
  if (minutes < 15) return 'text-green-500';
  if (minutes < 30) return 'text-yellow-500';
  return 'text-red-500';
};

// Função para obter cor de fundo do tempo de espera
const getWaitingTimeBgColor = (minutes) => {
  if (!minutes || isNaN(minutes)) return 'bg-gray-100';
  if (minutes < 15) return 'bg-green-100';
  if (minutes < 30) return 'bg-yellow-100';
  return 'bg-red-100';
};

const PendingConversationsTable = ({
  conversations = [],
  pagination = {},
  loading = false,
  onPageChange,
  onSortChange,
  sortField = 'waiting_time_minutes',
  sortDirection = 'desc',
  filters = {},
}) => {
  const { page = 1, total = 0, total_pages = 1, pending_return_count = 0 } = pagination;
  const [exporting, setExporting] = useState(false);

  const handleSort = (field) => {
    if (onSortChange) {
      const newDirection = sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
      onSortChange(field, newDirection);
    }
  };

  const SortableHeader = ({ field, children, className = '' }) => (
    <th
      scope="col"
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ArrowsUpDownIcon className="w-3 h-3 text-gray-300" />
        )}
      </div>
    </th>
  );

  const getConversationTypeLabel = (type) => {
    switch (type) {
      case 'active':
        return { label: 'Ativo', bgColor: 'bg-blue-100', textColor: 'text-blue-800' };
      case 'receptive':
        return { label: 'Receptivo', bgColor: 'bg-purple-100', textColor: 'text-purple-800' };
      default:
        return { label: 'Desconhecido', bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return { label: 'Aberto', bgColor: 'bg-green-100', textColor: 'text-green-800' };
      case 'pending':
        return { label: 'Pendente', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
      case 'resolved':
        return { label: 'Resolvido', bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
      case 'snoozed':
        return { label: 'Snoozed', bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' };
      default:
        return { label: status, bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);

      // Buscar TODAS as conversas pendentes (sem paginação)
      const params = {
        ...filters,
        page: 1,
        limit: 999999, // Buscar todos os registros
      };

      const response = await metricsService.getPendingConversations(params);
      const allConversations = response.data.data?.conversations || [];

      if (allConversations.length === 0) {
        alert('Nenhuma conversa para exportar');
        return;
      }

      // Preparar dados para Excel
      const excelData = allConversations.map(conv => {
        const teams = Array.isArray(conv.agent_teams) ? conv.agent_teams : [];
        const labels = Array.isArray(conv.labels) ? conv.labels : [];
        const typeInfo = getConversationTypeLabel(conv.conversation_type);
        const statusInfo = getStatusLabel(conv.status_label);

        return {
          'ID': conv.display_id,
          'Tipo de Conversa': typeInfo.label,
          'Status': statusInfo.label,
          'Tempo Aguardando (min)': conv.waiting_time_minutes || 0,
          'Tempo Aguardando (formatado)': formatMinutes(conv.waiting_time_minutes),
          'Pendente de Retorno': conv.pending_return ? 'Sim' : 'Não',
          'Agente': conv.agent_name || 'Sem agente',
          'Email do Agente': conv.agent_email || '-',
          'Canal': conv.inbox_name || '-',
          'Tipo de Canal': conv.channel_type || '-',
          'Equipes': teams.map(t => t.name).join(', ') || '-',
          'Etiquetas': labels.map(l => l.title).join(', ') || '-',
          'Nome do Contato': conv.contact_name || 'Sem nome',
          'Telefone': conv.contact_phone || '-',
          'Email': conv.contact_email || '-',
          'Data de Criação': conv.created_at ? new Date(conv.created_at).toLocaleString('pt-BR') : '-',
          'Última Atividade': conv.last_activity_at ? new Date(conv.last_activity_at).toLocaleString('pt-BR') : '-',
        };
      });

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Ajustar largura das colunas
      const colWidths = [
        { wch: 8 },  // ID
        { wch: 15 }, // Tipo de Conversa
        { wch: 12 }, // Status
        { wch: 20 }, // Tempo Aguardando (min)
        { wch: 25 }, // Tempo Aguardando (formatado)
        { wch: 18 }, // Pendente de Retorno
        { wch: 25 }, // Agente
        { wch: 30 }, // Email do Agente
        { wch: 20 }, // Canal
        { wch: 15 }, // Tipo de Canal
        { wch: 30 }, // Equipes
        { wch: 40 }, // Etiquetas
        { wch: 25 }, // Nome do Contato
        { wch: 15 }, // Telefone
        { wch: 30 }, // Email
        { wch: 20 }, // Data de Criação
        { wch: 20 }, // Última Atividade
      ];
      ws['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Conversas Pendentes');

      // Gerar nome do arquivo com data/hora
      const now = new Date();
      const fileName = `conversas_pendentes_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}h${String(now.getMinutes()).padStart(2, '0')}.xlsx`;

      // Download do arquivo
      XLSX.writeFile(wb, fileName);

    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-500">Carregando conversas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Conversas Pendentes de Retorno
            </h2>
            <span className="px-2 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
              {pending_return_count} aguardando
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              Total: {total} conversas
            </span>
            <button
              onClick={handleExportToExcel}
              disabled={exporting || total === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                exporting || total === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title="Exportar todos os registros para Excel"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Exportando...</span>
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Exportar Excel</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader field="display_id">ID</SortableHeader>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <SortableHeader field="waiting_time_minutes">Tempo Aguardando</SortableHeader>
              <SortableHeader field="agent_name">Agente</SortableHeader>
              <SortableHeader field="inbox_name">Canal</SortableHeader>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Equipe
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Etiquetas
              </th>
              <SortableHeader field="contact_name">Contato</SortableHeader>
              <SortableHeader field="status_label">Status</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {conversations.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                  Nenhuma conversa pendente encontrada
                </td>
              </tr>
            ) : (
              conversations.map((conv) => {
                const typeInfo = getConversationTypeLabel(conv.conversation_type);
                const statusInfo = getStatusLabel(conv.status_label);
                const teams = Array.isArray(conv.agent_teams) ? conv.agent_teams : [];
                const labels = Array.isArray(conv.labels) ? conv.labels : [];

                return (
                  <tr
                    key={conv.conversation_id}
                    className={`hover:bg-gray-50 ${conv.pending_return ? 'bg-red-50/50' : ''}`}
                  >
                    {/* ID */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => openChatwootConversation(conv.display_id)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                        title="Abrir no Chatwoot"
                      >
                        #{conv.display_id}
                        <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      </button>
                    </td>

                    {/* Tipo */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeInfo.bgColor} ${typeInfo.textColor}`}>
                        {typeInfo.label}
                      </span>
                    </td>

                    {/* Tempo Aguardando */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <ClockIcon className={`w-4 h-4 ${getWaitingTimeColor(conv.waiting_time_minutes)}`} />
                        <span className={`px-2 py-1 text-sm font-medium rounded ${getWaitingTimeBgColor(conv.waiting_time_minutes)} ${getWaitingTimeColor(conv.waiting_time_minutes)}`}>
                          {formatMinutes(conv.waiting_time_minutes)}
                        </span>
                      </div>
                    </td>

                    {/* Agente */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {conv.agent_name || 'Sem agente'}
                        </div>
                        {conv.agent_email && (
                          <div className="text-gray-500 text-xs truncate max-w-32">
                            {conv.agent_email}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Canal */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {conv.inbox_name || '-'}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {conv.channel_type}
                        </div>
                      </div>
                    </td>

                    {/* Equipe */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {teams.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {teams.slice(0, 2).map((team, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {team.name}
                            </span>
                          ))}
                          {teams.length > 2 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                              +{teams.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>

                    {/* Etiquetas */}
                    <td className="px-4 py-3">
                      {labels.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-40">
                          {labels.slice(0, 3).map((label, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full text-white"
                              style={{ backgroundColor: label.color || '#6b7280' }}
                            >
                              {label.title}
                            </span>
                          ))}
                          {labels.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                              +{labels.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>

                    {/* Contato */}
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 truncate max-w-32">
                          {conv.contact_name || 'Sem nome'}
                        </div>
                        {conv.contact_phone && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <PhoneIcon className="w-3 h-3" />
                            {conv.contact_phone}
                          </div>
                        )}
                        {conv.contact_email && !conv.contact_phone && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs truncate max-w-32">
                            <EnvelopeIcon className="w-3 h-3" />
                            {conv.contact_email}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          {statusInfo.label}
                        </span>
                        {conv.pending_return && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                            <ExclamationCircleIcon className="w-3 h-3" />
                            Aguardando retorno
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total_pages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Página {page} de {total_pages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange && onPageChange(page - 1)}
              disabled={page <= 1}
              className={`px-3 py-1 rounded text-sm font-medium ${
                page <= 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={() => onPageChange && onPageChange(page + 1)}
              disabled={page >= total_pages}
              className={`px-3 py-1 rounded text-sm font-medium ${
                page >= total_pages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingConversationsTable;
